import { useInputControl } from '@conform-to/react'
import React, { type ReactNode, useId, useRef, useState } from 'react'
import { cn } from '#app/shared/lib/misc.js'
import { omit } from '#app/shared/lib/objects.js'
import { dateToInputUTC, dateFromInputUTC } from '#app/shared/lib/strings.js'
import { Icon } from './icons/Icon.tsx'
import { Checkbox, type CheckboxProps } from './inputs/Checkbox.tsx'
import { Combobox, type ComboboxProps } from './inputs/Combobox.tsx'
import {
  ImageRadioGroup,
  type ImageRadioGroupProps,
} from './inputs/ImageRadioGroup.tsx'
import { Input, type InputProps } from './inputs/Input.tsx'
import { Label } from './inputs/Label.tsx'
import { RadioGroup, type RadioGroupProps } from './inputs/RadioGroup.tsx'
import { countries } from './inputs/select/Countries.ts'
import {
  type DatePickerProps,
  DatePickerSelect,
} from './inputs/select/DatePickerSelect.tsx'
import { Select, type SelectProps } from './inputs/select/Select.tsx'
import { StarsRating, type StarsRatingProps } from './inputs/Stars.tsx'
import { Textarea, type TextareaProps } from './inputs/Textarea.tsx'
import { TimeInput, type TimeInputProps } from './inputs/TimeInput.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
  id,
  errors,
}: {
  errors?: ListOfErrors
  id?: string
}) {
  const errorsToRender = errors?.filter(Boolean)
  if (!errorsToRender?.length) return null
  return (
    <ul id={id} className="flex flex-col gap-1">
      {errorsToRender.map((e) => (
        <li key={e} className="text-[10px] text-danger">
          {e}
        </li>
      ))}
    </ul>
  )
}

export function Field({
  labelProps,
  inputProps,
  errors,
  className,
  inputClassName,
}: {
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement> & {
    asChild?: boolean
  }
  inputProps: Partial<InputProps>
  errors?: ListOfErrors
  className?: string
  inputClassName?: string
}) {
  const fallbackId = useId()
  const id = inputProps.id ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined
  return (
    <div className={cn('block w-full', className)}>
      {labelProps && (
        <Label className="text-label" htmlFor={id} {...labelProps} />
      )}

      <Input
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        {...inputProps}
        className={cn(inputProps.className, errorId && '!border-danger')}
        inputClassName={inputClassName}
      />

      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}

export function PasswordField({
  labelProps,
  inputProps,
  errors,
  className,
}: {
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement> & {
    asChild?: boolean
  }
  inputProps: Partial<InputProps>
  errors?: ListOfErrors
  className?: string
}) {
  const fallbackId = useId()
  const id = inputProps.id ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined
  const [showPassword, setShowPassword] = useState(false)
  return (
    <div className={cn('block w-full', className)}>
      {labelProps && <Label htmlFor={id} {...labelProps} />}
      <div className="relative">
        <Input
          id={id}
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          {...inputProps}
          type={showPassword ? 'text' : 'password'}
          className={cn(inputProps.className, errorId && '!border-danger')}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className={`
            absolute bottom-0 right-4 flex

            h-[${inputProps.size}]

            w-8 cursor-pointer items-center justify-center rounded-full
            text-gray-400
          `}
        >
          <Icon
            size="24px"
            name={showPassword ? 'basic/eye' : 'basic/eye-closed'}
          />
        </button>
      </div>
      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}

export function TimeField({
  labelProps,
  inputProps,
  errors,
  className,
}: {
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement> & {
    asChild?: boolean
  }
  inputProps: Partial<TimeInputProps>
  errors?: ListOfErrors
  className?: string
}) {
  const fallbackId = useId()
  const id = inputProps.id ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined
  const variant = inputProps.variant ?? 'filled'

  return (
    <div className={cn('w-full', className)}>
      {labelProps && (
        <Label htmlFor={id} className="text-label" {...labelProps} />
      )}
      <TimeInput
        variant={variant}
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        {...inputProps}
      />
      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}

export function TextareaField({
  labelProps,
  textareaProps,
  errors,
  className,
  textareaClassName,
}: {
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  textareaProps: TextareaProps
  errors?: ListOfErrors
  className?: string
  textareaClassName?: string
}) {
  const fallbackId = useId()
  const id = textareaProps.id ?? textareaProps.name ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined
  return (
    <div className={className}>
      <Label htmlFor={id} className="text-label" {...labelProps} />
      <Textarea
        id={id}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
        {...textareaProps}
        className={cn(textareaProps.className, errorId && 'border-danger')}
        textareaClassName={textareaClassName}
      />
      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}

export function CheckboxField({
  labelProps,
  checkboxProps,
  errors,
  className,
}: {
  labelProps?: JSX.IntrinsicElements['label']
  checkboxProps: CheckboxProps & {
    name?: string
    form?: string
    value?: string
    variant?: string
    checkboxClassname?: string
  }
  errors?: ListOfErrors
  className?: string
}) {
  const { key, ...otherCheckboxProps } = checkboxProps
  const fallbackId = useId()
  const checkedValue = checkboxProps.value ?? 'on'

  // To emulate native events that Conform listen to:
  // See https://conform.guide/integrations
  const input = useInputControl({
    key,
    name: checkboxProps.name as string,
    formId: checkboxProps.form as string,
    initialValue: checkboxProps.defaultChecked ? checkedValue : undefined,
  })
  const id = checkboxProps.id ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined

  return (
    <div className={className}>
      <div className="flex gap-2">
        <Checkbox
          id={id}
          className={cn(
            `
              h-5 w-5 cursor-pointer appearance-none rounded-full border-2

              focus:outline-none
            `,
            checkboxProps.checkboxClassname,
          )}
          color="sexualDollsMansion"
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          {...otherCheckboxProps}
          onCheckedChange={(state) => {
            input.change(state.valueOf() ? checkedValue : '')
            checkboxProps.onCheckedChange?.(state)
          }}
          onFocus={(event) => {
            input.focus()
            checkboxProps.onFocus?.(event)
          }}
          onBlur={(event) => {
            input.blur()
            checkboxProps.onBlur?.(event)
          }}
          type="button"
        />
        <label
          htmlFor={id}
          {...labelProps}
          className={`
            cursor-pointer self-center text-[16px] leading-4
            text-foreground-muted

            hover:text-gray-900

            ${labelProps?.className || ''}
          `}
        />
      </div>
      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}

export type AutocompleteOption = {
  label: ReactNode
  labelTrigger?: ReactNode
  value: any
}

export function ComboboxField({
  labelProps,
  inputProps,
  errors,
  className,
}: {
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  inputProps: React.InputHTMLAttributes<HTMLInputElement> &
    ComboboxProps & {
      key: any
      name?: string
      form: string
      value?: string
    }
  errors?: ListOfErrors
  className?: string
}) {
  const fallbackId = useId()
  const {
    key,
    id: inputId,
    value: valueProp,
    defaultValue,
    ...otherInputProps
  } = inputProps
  const id = inputId ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined

  const value = valueProp ?? defaultValue

  const shadowInputRef = useRef<any>(null)
  const input = useInputControl({
    key,
    name: inputProps.name as any,
    formId: inputProps.form,
    initialValue: value,
  })

  return (
    <div className={cn(className)}>
      {labelProps && <Label htmlFor={id} {...labelProps} />}
      <input
        ref={shadowInputRef}
        {...otherInputProps}
        value={value}
        hidden={true}
      />
      <Combobox
        id={id}
        {...{ ...(inputProps as any) }}
        value={value}
        onChange={(value) => {
          if (!value) return
          input.change(String(value))
          // inputProps.onChange?.(value as any)
        }}
        onBlur={input.blur}
        onFocus={input.focus}
      />
      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}

export function RadioGroupField({
  labelProps,
  inputProps,
  errors,
  className,
}: {
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  inputProps: RadioGroupProps &
    React.InputHTMLAttributes<HTMLInputElement> & {
      key?: any
      name?: string
      form?: string
      value?: string
    }
  errors?: ListOfErrors
  className?: string
}) {
  const fallbackId = useId()
  const { id: inputId, key, ...otherInputProps } = inputProps
  const id = inputId ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined

  // to be able to trigger conform events, we use hidden input with shadowRef
  // more info https://conform.guide/integrations
  const shadowInputRef = useRef<any>(null)
  const input = useInputControl({
    key,
    name: inputProps.name as any,
    formId: inputProps.form as any,
    initialValue: inputProps.defaultValue,
  })

  return (
    <div className={className}>
      <Label htmlFor={id} {...labelProps} />
      <input ref={shadowInputRef} {...otherInputProps} className="hidden" />
      <RadioGroup
        id={id}
        {...(inputProps as any)}
        onChange={(value) => {
          if (!value) return
          input.change(value)
          input.blur()
          inputProps.onChange?.(value as any)
        }}
        // onBlur={control.blur}
        onFocus={input.focus}
      />
      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}

export function ImageRadioGroupField({
  inputProps,
  errors,
  className,
}: {
  inputProps: ImageRadioGroupProps &
    React.InputHTMLAttributes<HTMLInputElement> & {
      key?: any
      name?: string
      form?: string
      value?: string
    }
  errors?: ListOfErrors
  className?: string
}) {
  const fallbackId = useId()
  const { id: inputId, key, ...otherInputProps } = inputProps
  const id = inputId ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined

  // Hidden input for Conform integration
  const shadowInputRef = useRef<HTMLInputElement>(null)
  const input = useInputControl({
    key,
    name: inputProps.name as any,
    formId: inputProps.form as any,
    initialValue: inputProps.defaultValue,
  })

  return (
    <div className={className}>
      <input
        ref={shadowInputRef}
        type="radio"
        {...otherInputProps}
        className="hidden"
      />
      <ImageRadioGroup
        {...(otherInputProps as ImageRadioGroupProps)}
        value={input.value as string}
        onChange={(value) => {
          if (!value) return
          input.change(value)
          input.blur()
          shadowInputRef.current?.dispatchEvent(
            new Event('change', { bubbles: true }),
          )
          inputProps.onChange?.(value as any)
        }}
      />
      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}

export function DatePickerField({
  labelProps,
  inputProps,
  errors,
  className,
}: {
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  inputProps: React.InputHTMLAttributes<HTMLInputElement> &
    DatePickerProps & {
      key: any
      name: string
      form: string
      value?: string | Date
    }
  errors?: ListOfErrors
  className?: string
}) {
  const fallbackId = useId()
  const { id: inputId, key, ...otherInputProps } = inputProps
  const id = inputId ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined

  // to be able to trigger conform events, we use hidden input with shadowRef
  // more info https://conform.guide/integrations
  const [value, setValue] = useState(() => {
    const initialValue = inputProps.defaultValue ?? inputProps.value ?? ''
    // If we have an initial date value, convert it to input format for editing
    if (
      initialValue &&
      initialValue !== '' &&
      (typeof initialValue === 'string' || initialValue instanceof Date)
    ) {
      return dateToInputUTC(initialValue) || ''
    }
    return ''
  })

  const shadowInputRef = useRef<any>(null)
  const input = useInputControl({
    key,
    name: inputProps.name,
    formId: inputProps.form,
  })

  return (
    <div className={className}>
      <input ref={shadowInputRef} {...otherInputProps} hidden={true} />
      {labelProps && <Label htmlFor={id} {...labelProps} />}
      <DatePickerSelect
        {...{ ...(inputProps as any) }}
        readOnly={inputProps.disabled}
        onChange={(date) => {
          const newValue = dateToInputUTC(date)
          if (!newValue) return
          setValue(newValue)
          input.change(newValue)
          input.blur()
          inputProps.onChange?.(newValue as any)
        }}
        date={value ? dateFromInputUTC(String(value)) : undefined}
        onFocus={input.focus}
      />
      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}

export function SelectField({
  labelProps,
  inputProps,
  errors,
  className,
}: {
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  inputProps: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> &
    SelectProps & {
      key?: any
      name?: string
      form?: string
      value?: string
    }
  errors?: ListOfErrors
  className?: string
}) {
  const fallbackId = useId()
  const { id: inputId, key, size, ...otherInputProps } = inputProps
  const id = inputId ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined

  const shadowInputRef = useRef<any>(null)
  const control = useInputControl({
    key,
    name: String(inputProps.name),
    formId: String(inputProps.form),
  })

  return (
    <div className={className}>
      {labelProps && <Label htmlFor={id} {...labelProps} />}
      <input
        ref={shadowInputRef}
        {...omit(
          otherInputProps,
          'triggerClassName',
          'optionsClassName',
          'optionClassName',
          'chevronIconClassName',
          'checkIconClassName',
          'variant',
          'leftIcon',
          'size',
        )}
        hidden={true}
      />
      <Select
        id={id}
        {...(inputProps as any)}
        size={size}
        onChange={(value: any) => {
          control.change(value)
          control.blur()
          inputProps.onChange?.(value)
        }}
        triggerClassName={cn(
          inputProps.triggerClassName,
          errorId && 'border-danger',
        )}
        onFocus={control.focus}
      />
      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}

export function StarsRatingField({
  labelProps,
  inputProps,
  errors,
  className,
}: {
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>
  inputProps: StarsRatingProps & {
    key: any
    name: string
    form: string
    value?: string
  }
  errors?: ListOfErrors
  className?: string
}) {
  const fallbackId = useId()
  // eslint-disable-next-line no-unused-vars
  const { key, id: inputId, size, onChange, ...otherInputProps } = inputProps
  const id = inputId ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined

  // to be able to trigger conform events, we use hidden input with shadowRef
  // more info https://conform.guide/integrations
  const [value, setValue] = useState(
    inputProps.defaultValue ?? inputProps.value ?? '',
  )

  const shadowInputRef = useRef<any>(null)
  const input = useInputControl({
    key,
    name: inputProps.name,
    formId: inputProps.form,
  })

  return (
    <div className={className}>
      {labelProps && <Label htmlFor={id} {...labelProps} />}
      <input ref={shadowInputRef} {...otherInputProps} hidden={true} />
      <StarsRating
        id={id}
        editable
        {...{ ...(inputProps as any), value, setValue }}
        onChange={(value: number) => {
          input.change(String(value))
          inputProps.onChange?.(value as any)
        }}
        onBlur={input.blur}
        onFocus={input.focus}
      />
      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}

export function PhoneField({
  labelProps,
  inputProps,
  errors,
  className,
  fullPhone,
  triggerClassName,
}: {
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement> & {
    asChild?: boolean
  }
  inputProps: Partial<InputProps>
  errors?: ListOfErrors
  className?: string
  fullPhone?: string
  triggerClassName?: string
}) {
  const fallbackId = useId()
  const id = inputProps.id ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined

  const [selectedCountry, setSelectedCountry] = useState(countries[0]?.code)
  const [userInputValue, setUserInputValue] = useState('')
  const [finalValue, setFinalValue] = useState(`${countries[0]?.prefix}`)

  const handleCountryChange = (value: string) => {
    const selected = countries.find((country) => country.code === value)
    if (selected) {
      const updatedValue = `${selected.prefix}${userInputValue}`
      setFinalValue(updatedValue)
      setSelectedCountry(value)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    setUserInputValue(inputValue)
    const selectedPrefix = countries.find(
      (country) => country.code === selectedCountry,
    )?.prefix
    setFinalValue(`${selectedPrefix || ''}${inputValue}`)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === '-' || e.key === '.' || e.key === 'e') {
      e.preventDefault()
    }
  } // this values make the forms fail for the phone field with 'required' error

  return (
    <div className={cn('block w-full', className)}>
      {labelProps && (
        <Label className="text-label mb-2" htmlFor={id} {...labelProps} />
      )}

      <div className="flex items-center">
        <Select
          options={countries.map((country) => ({
            label: country.flag + ' ' + country.prefix,
            value: country.code,
          }))}
          value={selectedCountry}
          onChange={handleCountryChange}
          className={cn(
            'w-32 min-w-32 rounded-l-lg rounded-r-none text-neutral-500',
            inputProps.className,
          )}
          triggerClassName={cn(
            'rounded-l-lg rounded-r-none bg-white text-center',
            triggerClassName,
          )}
          checkIconClassName="text-primary"
          optionsClassName="bg-white z-20"
        />
        <Input
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          {...inputProps}
          value={userInputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          inputClassName="rounded-l-none rounded-r-lg placeholder:text-neutral-500"
          className={cn(
            'rounded-l-none rounded-r-lg',
            inputProps.className,
            errorId && '!border-danger',
          )}
        />
        <input
          id={id}
          type="hidden"
          name={fullPhone}
          className="hidden"
          value={finalValue}
        />
      </div>

      {errorId && (
        <div className="px-4 pt-1">
          <ErrorList id={errorId} errors={errors} />
        </div>
      )}
    </div>
  )
}
